import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Check, X, RefreshCw, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatRelative } from "../lib/inboxApi";

/**
 * Sub-section dentro da aba "Orações" do /admin, modera pedidos que entraram
 * pelo formulário público da /landing (approved=false).
 */
export default function AdminPendingPrayers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // id atualmente sendo processado

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/admin/prayers/pending");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar pendentes.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/prayers/${id}/approve`);
      setItems((arr) => arr.filter((p) => p.id !== id));
      toast.success("Aprovado para o mural.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao aprovar.");
    } finally { setBusy(null); }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Rejeitar este pedido? A ação é permanente.")) return;
    setBusy(id);
    try {
      await api.delete(`/admin/prayers/${id}`);
      setItems((arr) => arr.filter((p) => p.id !== id));
      toast.success("Pedido rejeitado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao rejeitar.");
    } finally { setBusy(null); }
  };

  return (
    <section data-testid="admin-pending-prayers" className="bg-white border border-border rounded-2xl p-5 sm:p-6 mb-6">
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold flex items-center gap-1.5">
            <Heart className="w-3 h-3" /> Mural público
          </p>
          <h3 className="font-display text-xl text-navy mt-1">Orações pendentes de aprovação</h3>
          <p className="text-[12px] text-navy/55 mt-1.5">
            Pedidos enviados pelo formulário público da <code className="text-navy">/landing</code>.
            Aprovar publica no mural; rejeitar apaga permanentemente.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="pending-prayers-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border text-[11px] uppercase tracking-widest text-navy/75 hover:border-gold/60 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </header>

      {loading ? (
        <div className="text-center text-navy/55 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-navy/55" data-testid="pending-prayers-empty">Nenhum pedido aguardando aprovação.</p>
      ) : (
        <ul className="space-y-3" data-testid="pending-prayers-list">
          {items.map((p) => (
            <li
              key={p.id}
              data-testid={`pending-prayer-${p.id}`}
              className="bg-[hsl(var(--parchment))] rounded-xl p-4 border border-border/50"
            >
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium text-navy">{p.name || "Anônimo"}</p>
                  <p className="text-[10px] uppercase tracking-widest text-navy/45 mt-0.5">
                    {formatRelative(p.created_at)}
                    {p.is_public ? <span className="text-emerald-600 ml-2">publicar no mural</span> : <span className="text-navy/55 ml-2">somente intercessão (não publicar)</span>}
                    {p.source && <span className="text-navy/45 ml-2">· {p.source}</span>}
                  </p>
                </div>
              </div>
              <p className="text-sm text-navy/85 whitespace-pre-line mb-3">{p.request}</p>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleApprove(p.id)}
                  disabled={busy === p.id}
                  data-testid={`pending-approve-${p.id}`}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 text-[11px] uppercase tracking-widest disabled:opacity-50"
                >
                  {busy === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Aprovar
                </button>
                <button
                  type="button"
                  onClick={() => handleReject(p.id)}
                  disabled={busy === p.id}
                  data-testid={`pending-reject-${p.id}`}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-50 text-[11px] uppercase tracking-widest disabled:opacity-50"
                >
                  <X className="w-3 h-3" />
                  Rejeitar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
